import React, { useMemo } from 'react';
import InfoTooltip from '../InfoTooltip';
import { useNumberLocale, formatFixed } from '../../../utils/numberFormat';
import { useChartFormatters, getYearEndTicks, formatYearTickLabel } from '../chartFormatters';
import TailwindBarChart from '../../Tailwind/TailwindBarChart';
import TailwindLineChart from '../../Tailwind/TailwindLineChart';
import { useChartPalette } from '../../Tailwind/chartColors';

function SummaryCard({
    label,
    value,
    info,
}: {
    label: string;
    value: React.ReactNode;
    info?: React.ReactNode;
}) {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
                {info && <InfoTooltip content={info} />}
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
        </div>
    );
}

const DISTRIBUTIONS_INFO: Record<string, React.ReactNode> = {
    total_distributions: (
        <>
            <strong>Total Distributions</strong> — Cash returned to investors to date.
            <br />
            <em>Formula:</em> Σ (dividends_up + shareholder_loan_up_interest + shareholder_loan_up_principal), pro-rated by equity_percentage.
        </>
    ),
    dpi: (
        <>
            <strong>DPI</strong> — Distributions to Paid-In capital.
            <br />
            Realized return only: cash distributed back to investors as a multiple of cash called.
            <br />
            <em>Formula:</em> Σ Distributions / Σ Paid-In (Drawdowns).
            <br />
            DPI ≥ 1.0x means investors have at least gotten their money back in cash.
        </>
    ),
    tvpi: (
        <>
            <strong>TVPI</strong> — Total Value to Paid-In capital.
            <br />
            Total value (realized + unrealized) per unit of paid-in capital.
            <br />
            <em>Formula:</em> (Σ Distributions + Residual NAV) / Σ Paid-In.
            <br />
            <em>Note:</em> residual NAV is 0 in the current implementation, so TVPI temporarily equals DPI.
        </>
    ),
    payback: (
        <>
            <strong>Payback (Simple)</strong> — Undiscounted payback period in months.
            <br />
            Time until the portfolio has returned its invested capital in raw cash. The point where the J-curve crosses zero.
            <br />
            <em>Formula:</em> first month i {'>'} 0 where cumsum(Distributions − Drawdowns) ≥ 0.
        </>
    ),
};

export default function DistributionsTab({ data }: { data: any }) {
    const numberLocale = useNumberLocale();
    const { formatYAxisTick, formatTooltipValue } = useChartFormatters();
    const palette = useChartPalette();
    const primaryColor = palette[0];
    const secondaryColor = palette[1 % palette.length];

    const distributionsChartData = useMemo(() => {
        const ts = data.distributions_ts || {};
        return Object.entries(ts).map(([date, value]: [string, any]) => ({
            date: date.substring(0, 7),
            distributions: value,
        }));
    }, [data.distributions_ts]);

    const jCurveChartData = useMemo(() => {
        const ts = data.j_curve_ts || {};
        return Object.entries(ts).map(([date, value]: [string, any]) => ({
            date: date.substring(0, 7),
            value: value,
        }));
    }, [data.j_curve_ts]);

    const cumulativeDistData = useMemo(() => {
        const ts = data.cumulative_distributions_ts || {};
        return Object.entries(ts).map(([date, value]: [string, any]) => ({
            date: date.substring(0, 7),
            cumulative: value,
        }));
    }, [data.cumulative_distributions_ts]);

    const distributionsTicks = useMemo(() => getYearEndTicks(distributionsChartData.map(d => d.date)), [distributionsChartData]);
    const jCurveTicks = useMemo(() => getYearEndTicks(jCurveChartData.map(d => d.date)), [jCurveChartData]);
    const cumulativeDistTicks = useMemo(() => getYearEndTicks(cumulativeDistData.map(d => d.date)), [cumulativeDistData]);

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryCard
                    label="Total Distributions"
                    value={data.total_distributions?.toLocaleString(numberLocale, { maximumFractionDigits: 0 }) || '—'}
                    info={DISTRIBUTIONS_INFO.total_distributions}
                />
                <SummaryCard
                    label="DPI"
                    value={data.dpi != null ? formatFixed(data.dpi, numberLocale, 2) + 'x' : '—'}
                    info={DISTRIBUTIONS_INFO.dpi}
                />
                <SummaryCard
                    label="TVPI"
                    value={data.tvpi != null ? formatFixed(data.tvpi, numberLocale, 2) + 'x' : '—'}
                    info={DISTRIBUTIONS_INFO.tvpi}
                />
                <SummaryCard
                    label="Payback"
                    value={data.payback_simple_months != null ? data.payback_simple_months + ' mo' : '—'}
                    info={DISTRIBUTIONS_INFO.payback}
                />
            </div>

            {/* Distributions Bar Chart */}
            {distributionsChartData.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Distribution Timeline</h3>
                    <TailwindBarChart
                        data={distributionsChartData}
                        xAxisKey="date"
                        height={300}
                        showLegend={false}
                        series={[{
                            dataKey: 'distributions',
                            label: 'Distributions',
                            color: primaryColor,
                            radius: [2, 2, 0, 0],
                        }]}
                        xAxisTicks={distributionsTicks}
                        xAxisTickFormatter={formatYearTickLabel}
                        xAxisTickFontSize={11}
                        yAxisTickFontSize={11}
                        yAxisTickFormatter={formatYAxisTick}
                        tooltipValueFormatter={formatTooltipValue}
                        brush={{ minDataPoints: 6 }}
                    />
                </section>
            )}

            {/* J-Curve Chart */}
            {jCurveChartData.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">J-Curve (Cumulative Net Cash Flow)</h3>
                    <TailwindLineChart
                        data={jCurveChartData}
                        xAxisKey="date"
                        height={300}
                        showLegend={false}
                        series={[{
                            dataKey: 'value',
                            label: 'Cumulative Net CF',
                            color: secondaryColor,
                            strokeWidth: 2,
                            showDots: false,
                        }]}
                        xAxisTicks={jCurveTicks}
                        xAxisTickFormatter={formatYearTickLabel}
                        xAxisTickFontSize={11}
                        yAxisTickFontSize={11}
                        yAxisTickFormatter={formatYAxisTick}
                        tooltipValueFormatter={formatTooltipValue}
                        brush={{ minDataPoints: 6 }}
                    />
                </section>
            )}

            {/* Cumulative Distributions */}
            {cumulativeDistData.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Cumulative Distributions</h3>
                    <TailwindLineChart
                        data={cumulativeDistData}
                        xAxisKey="date"
                        height={300}
                        showLegend={false}
                        series={[{
                            dataKey: 'cumulative',
                            label: 'Cumulative Distributions',
                            color: primaryColor,
                            strokeWidth: 2,
                            showDots: false,
                        }]}
                        xAxisTicks={cumulativeDistTicks}
                        xAxisTickFormatter={formatYearTickLabel}
                        xAxisTickFontSize={11}
                        yAxisTickFontSize={11}
                        yAxisTickFormatter={formatYAxisTick}
                        tooltipValueFormatter={formatTooltipValue}
                        brush={{ minDataPoints: 6 }}
                    />
                </section>
            )}

            {/* Quarterly Summary Table — distributions view */}
            {data.quarterly_summaries && data.quarterly_summaries.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Quarterly Summary</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Quarter</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Distribution</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Cumul. Distribution</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Assets</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.quarterly_summaries.map((q: any, i: number) => (
                                    <tr key={i} className="border-b border-slate-100 last:border-b-0">
                                        <td className="px-4 py-2 font-medium text-slate-900">{q.quarter}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.distributions?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.cumulative_distributions?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.num_assets_active}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}
